import React, { useEffect, useMemo, useRef, useState } from "react";
import { useEditor } from "../store.js";
import { usePhone } from "../../hooks/useMedia.js";
import { buildModel, tickStep, fmtTick, fmtTime, sameSel, type Sel } from "./model.js";
import { startDrag } from "./drags.js";
import { Strip } from "./Strip.js";
import { Layers } from "./Layers.js";
import { Tools } from "./Tools.js";
import { Levels } from "./Levels.js";

const loadLocked = (): boolean => { try { return localStorage.getItem("hudhud.lockPieces") === "1"; } catch { return false; } };

/**
 * The timeline: a strip of time that scrolls under a playhead fixed in the middle, so scrolling is scrubbing, on a phone and on
 * the desk alike. While the clip plays the strip glides along under the line; a finger or the wheel takes over for as long as it
 * scrubs, and playback carries on from wherever it lets go. One video strip, slim layers under it, one tool bar, one menu.
 */
export const Timeline: React.FC = () => {
  const clip = useEditor((s) => s.clip);
  const thumbs = useEditor((s) => s.thumbs);
  const zoom = useEditor((s) => s.timelineZoom);
  const playing = useEditor((s) => s.playing);
  const playheadMs = useEditor((s) => s.playheadMs);
  const selectedPiece = useEditor((s) => s.selectedPiece);
  const phone = usePhone();
  const scroller = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const [viewW, setViewW] = useState(800);
  const [sel, setSelState] = useState<Sel>(null);
  const [dropAt, setDropAt] = useState<number | null>(null);
  const [locked, setLockedState] = useState(loadLocked);
  const setLocked = (v: boolean) => { setLockedState(v); try { localStorage.setItem("hudhud.lockPieces", v ? "1" : "0"); } catch { /* ignore */ } };
  const stripH = phone ? 64 : 56, rowH = phone ? 26 : 22;

  useEffect(() => { const el = scroller.current; if (!el) return; const ro = new ResizeObserver(() => setViewW(Math.max(120, el.clientWidth))); ro.observe(el); setViewW(Math.max(120, el.clientWidth)); return () => ro.disconnect(); }, []);
  const model = useMemo(() => (clip ? buildModel(clip, viewW, zoom) : null), [clip, viewW, zoom]);
  const px = model?.px ?? 1, totalMs = model?.totalMs ?? 1;
  const pad = viewW / 2;
  const x = (ms: number) => pad + ms * px;
  // a phone opens on about 15 s across the screen; the desk on the whole clip
  useEffect(() => { if (phone && clip && useEditor.getState().timelineZoom === 1) useEditor.getState().setTimelineZoom(Math.max(1, totalMs / 15000)); }, [clip?.id, phone]);

  /** select something (or nothing) on the strip, and usually seek to it */
  const select = (s: Sel, seekMs?: number) => {
    setSelState((cur) => (sameSel(cur, s) ? cur : s));
    const st = useEditor.getState();
    st.setSelectedPiece(s?.kind === "piece" ? s.i : null);
    st.setSelectedCut(s?.kind === "cut" ? s.i : null);
    st.setSelectedOverlay(s?.kind === "picture" ? s.id : null);
    if (seekMs != null) { st.setPlaying(false); st.setPlayhead(Math.max(0, Math.min(totalMs - 1, seekMs))); }
  };
  // the keyboard moves or clears the selected piece through the store: mirror it here
  useEffect(() => {
    if (selectedPiece != null && !(sel?.kind === "piece" && sel.i === selectedPiece)) setSelState({ kind: "piece", i: selectedPiece });
    if (selectedPiece == null && sel?.kind === "piece") setSelState(null);
  }, [selectedPiece]);
  useEffect(() => { if (model && ((sel?.kind === "piece" && sel.i >= model.blocks.length) || (sel?.kind === "cut" && sel.i > model.blocks.length - 1))) select(null); }, [model?.blocks.length]);
  // a transition dropped on the timeline from the gallery selects the cut it landed on
  const selectedCut = useEditor((s) => s.selectedCut);
  useEffect(() => { if (selectedCut != null && !(sel?.kind === "cut" && sel.i === selectedCut)) setSelState({ kind: "cut", i: selectedCut }); if (selectedCut == null && sel?.kind === "cut") setSelState(null); }, [selectedCut]);
  useEffect(() => { const k = (e: KeyboardEvent) => { if (e.key === "Escape") select(null); }; window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, []);

  // ---- scrolling is scrubbing
  const prog = useRef(0);                 // when the strip was last moved by the code, not the owner
  const hold = useRef(false);             // a finger is on the strip: the code stops moving it
  const scrub = useRef<{ resume: boolean } | null>(null); // the owner is scrubbing; whether to play on again after
  const idle = useRef<ReturnType<typeof setTimeout> | null>(null);
  const endScrubSoon = (ms: number) => { if (idle.current) clearTimeout(idle.current); idle.current = setTimeout(endScrub, ms); };
  const beginScrub = () => { if (scrub.current) return; const st = useEditor.getState(); scrub.current = { resume: st.playing }; if (st.playing) st.setPlaying(false); };
  const endScrub = () => { idle.current = null; hold.current = false; const s = scrub.current; scrub.current = null; if (s?.resume) useEditor.getState().setPlaying(true); };
  const onScroll = () => {
    const el = scroller.current;
    if (!el || !model) return;
    if (!scrub.current && (performance.now() - prog.current < 150 || !hold.current) && useEditor.getState().playing) return; // the code gliding the strip along
    if (performance.now() - prog.current < 150 && !hold.current && !scrub.current) return; // a seek from elsewhere landing
    if (hold.current || scrub.current) { beginScrub(); endScrubSoon(phone ? 320 : 180); }
    const ms = Math.max(0, Math.min(totalMs, el.scrollLeft / px));
    useEditor.getState().scrubTo(ms);
  };
  // a seek from elsewhere (the words, the transport, the preview) brings the strip to it
  useEffect(() => {
    const el = scroller.current;
    if (!el || playing || scrub.current) return;
    const target = playheadMs * px;
    if (Math.abs(el.scrollLeft - target) > 0.5) { prog.current = performance.now(); el.scrollLeft = target; }
  }, [playheadMs, px, playing]);
  // while playing the strip glides: the store's playhead arrives ~12× a second, the frames in between are predicted
  useEffect(() => {
    const el = scroller.current;
    if (!el || !playing) return;
    let raf = 0, last = { ms: useEditor.getState().playheadMs, at: performance.now() };
    const unsub = useEditor.subscribe((s, prev) => { if (s.playheadMs !== prev.playheadMs) last = { ms: s.playheadMs, at: performance.now() }; });
    const tick = () => {
      if (!hold.current && !scrub.current) { const ms = Math.min(totalMs, last.ms + (performance.now() - last.at) * useEditor.getState().speed); prog.current = performance.now(); el.scrollLeft = ms * px; }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => { cancelAnimationFrame(raf); unsub(); };
  }, [playing, px, totalMs]);
  // the wheel scrubs (a plain wheel too), ⌘/ctrl + wheel zooms
  useEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) { e.preventDefault(); zoomBy(e.deltaY < 0 ? 1.15 : 1 / 1.15); return; }
      hold.current = true; beginScrub(); endScrubSoon(220);
      if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) { e.preventDefault(); el.scrollLeft += e.deltaY; }
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [px, totalMs]);
  const zoomBy = (f: number) => useEditor.getState().setTimelineZoom(useEditor.getState().timelineZoom * f);
  const fit = () => useEditor.getState().setTimelineZoom(1);
  // fingers: one pans (the browser scrolls, we scrub), two pinch to zoom
  const touches = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ d0: number; z0: number } | null>(null);
  const onPointerDown = (e: React.PointerEvent) => {
    if (e.pointerType === "touch") {
      touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (touches.current.size === 2) { const [a, b] = [...touches.current.values()]; pinch.current = { d0: Math.max(1, Math.hypot(a.x - b.x, a.y - b.y)), z0: useEditor.getState().timelineZoom }; }
      hold.current = true; // the strip is the owner's until the finger lifts (and its momentum ends)
      return;
    }
    // the mouse: dragging the background scrubs; a plain click on it seeks and clears the selection (blocks and chips handle themselves)
    if ((e.target as HTMLElement).closest("[data-grab], [role=button], button, .tl-block, .tl-chip")) return;
    const el = scroller.current!;
    const start = el.scrollLeft;
    hold.current = true;
    startDrag(e, {
      move: (dx) => { beginScrub(); el.scrollLeft = start - dx; endScrubSoon(180); },
      end: (moved, ev) => { if (!moved) { const r = el.getBoundingClientRect(); const ms = (el.scrollLeft + (ev.clientX - r.left) - pad) / px; select(null, ms); hold.current = false; } else endScrubSoon(120); },
    });
  };
  const onPointerMove = (e: React.PointerEvent) => { if (!touches.current.has(e.pointerId)) return; touches.current.set(e.pointerId, { x: e.clientX, y: e.clientY }); if (pinch.current && touches.current.size >= 2) { const [a, b] = [...touches.current.values()]; useEditor.getState().setTimelineZoom(Math.max(0.5, Math.min(12, pinch.current.z0 * (Math.hypot(a.x - b.x, a.y - b.y) / pinch.current.d0)))); } };
  const onPointerUp = (e: React.PointerEvent) => { if (e.pointerType !== "touch") return; touches.current.delete(e.pointerId); if (touches.current.size < 2) pinch.current = null; if (!touches.current.size) endScrubSoon(phone ? 320 : 180); };
  // a tap on the empty strip (touch) seeks there and clears the selection
  const onTapBackground = (e: React.MouseEvent) => { if (e.detail === 0) return; const t = e.target as HTMLElement; if (t.closest("[data-grab], button, [role=button]")) return; const el = scroller.current!; const r = el.getBoundingClientRect(); select(null, (el.scrollLeft + (e.clientX - r.left) - pad) / px); };

  if (!clip || !model) return null;
  const step = tickStep(px);
  const ticks: React.ReactNode[] = [];
  for (let ms = 0; ms < totalMs; ms += step) ticks.push(<span key={ms} className="tl-tick" style={{ left: x(ms) }}>{fmtTick(ms, step)}</span>);
  const originX = () => (content.current?.getBoundingClientRect().left ?? 0) + pad;
  return (
    <div className={`tl${phone ? " phone" : ""}`}>
      <Tools clip={clip} model={model} sel={sel} select={(s) => select(s)} phone={phone} locked={locked} setLocked={setLocked} zoomBy={zoomBy} fit={fit} />
      <div className="tl-view">
        <div className="tl-main">
        <div ref={scroller} className="tl-scroll" onScroll={onScroll} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp} onClick={phone ? onTapBackground : undefined} role="group" aria-label="Timeline: scroll to move through the clip">
          <div ref={content} className="tl-content" style={{ width: pad * 2 + totalMs * px }}>
            <div className="tl-ruler" data-bg="1"><span className="tl-lbl" aria-hidden="true" /> {ticks}</div>
            <Strip clip={clip} model={model} sel={sel} select={select} x={x} originX={originX} phone={phone} locked={locked} dropAt={dropAt} setDropAt={setDropAt} height={stripH} thumbs={thumbs} />
            <Layers clip={clip} model={model} sel={sel} select={select} x={x} phone={phone} locked={locked} rowH={rowH} />
          </div>
        </div>
        <div className="tl-head" aria-hidden="true">{phone ? null : <span className="tl-now num">{fmtTime(playheadMs)}.{Math.floor((playheadMs % 1000) / 100)}</span>}</div>
        </div>
        <Levels clip={clip} />
      </div>
    </div>
  );
};
